import { Formik } from "formik";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import React, { useEffect, useState } from "react";
import { Image, View, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Text from "./AppText";
import { notify } from "./AppToast";
import {
  donationPresentation,
  isDonationMessage,
} from "../donations/presentation";

import { useActionSheet } from "@/components/hooks/useActionSheet";
import {
  deleteComment,
  fetchGuestbook,
  subToGuestbook,
  updateEventPrivacy,
} from "../http/events";
import IconButton from "@/components/settings/IconButton";
import AutoSave from "./AutoSave";
import GuestbookPinnedMessageForm from "./GuestbookPinnedMessageForm";
import { ChoiceGroup } from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { colors, text } from "@/components/settings/theme";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import tw from "twrnc";
import { t, tn } from "@/localization";

const RESIZED_BUCKET =
  "https://eventlive-guest-upload-resized.s3.amazonaws.com";

const resizedImageUrl = (image_filename) => {
  const split = image_filename.split(".");
  split.pop();
  const name = split.join("");
  return `${RESIZED_BUCKET}/${name}.jpg`;
};

const GuestBook = ({ event: initialEvent }) => {
  const { showActionSheetWithOptions } = useActionSheet();
  const [loading, setloading] = useState(true);
  const { slug } = initialEvent;

  const [event, setEvent] = useState(initialEvent);
  // Teams: moderators moderate and pin. The mode form saves every privacy
  // setting (the event password too), so it's for editors and up.
  const team = useTeamAccess();
  const canChangeMode = team.can("editEvent", event);
  const canPin = team.can("pinMessage", event);

  useEffect(() => {
    setEvent((currentEvent) => ({
      ...currentEvent,
      ...initialEvent,
    }));
  }, [initialEvent]);

  const event_id = event.id;

  const fetchAsync = async () => {
    try {
      const myEvent = await fetchGuestbook(slug);
      if (!myEvent) {
        throw new Error("Not found");
      }
      setEvent((currentEvent) => ({
        ...currentEvent,
        ...myEvent,
      }));
      setloading(false);
    } catch (err) {
      console.error(err);
      // Show Banner not found and redirect to LIST
      notify.error(
        t("Couldn't load the guestbook"),
        t("Check your connection and try again.")
      );
    }
  };

  useEffect(() => {
    fetchAsync();
  }, [slug]);

  const onMessageDelete = (id) => () => {
    showActionSheetWithOptions(
      {
        options: [t("Delete"), t("Cancel")],
        cancelButtonIndex: 1,
        destructiveButtonIndex: 0,
        title: t("Delete this message?"),
      },
      async (buttonIndex) => {
        if (buttonIndex === 0) {
          setEvent({
            ...event,
            guestbook_messages: event.guestbook_messages.filter(
              (m) => m.id !== id
            ),
          });
          // Delete comment
          await deleteComment(event, id);
        }
      }
    );
  };

  useEffect(() => {
    if (event_id) {
      const subscription = subToGuestbook(event_id, fetchAsync);

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [event_id]);

  const messages = (event.guestbook_messages || []).sort((a, b) => {
    return a.message_datetime_utc > b.message_datetime_utc ? -1 : 1;
  });

  const guestbookTitle =
    event.guestbook_enabled === false
      ? t("Messaging")
      : event.prefer_chat
      ? t("Chat")
      : t("Guestbook");

  const authorLine = (item) => {
    // In someone else's account the pin may be the holder's or a teammate's.
    if (item.pinned) return team.isShared ? t("Pinned message") : t("You pinned");
    const name = item.guest?.name || "";
    const email = item.guest?.email ? `(${item.guest.email})` : "";
    return [name, email].filter(Boolean).join(" ");
  };

  const renderMessage = (item) => {
    const donation = isDonationMessage(item)
      ? donationPresentation(item)
      : null;
    return (
      <View
        style={[
          tw`rounded-tl-[14px] rounded-tr-[14px] rounded-bl-[3px] rounded-br-[14px] mb-[10px] p-4 pr-12`,
          {
            backgroundColor: item.pinned
              ? colors.brandTint
              : colors.surfaceStrong,
          },
        ]}
        key={item.id}
      >
        <Text style={text.bodyStrong}>
          {donation ? donation.name : authorLine(item)}
        </Text>
        <Text style={text.hint}>
          {moment(item.message_datetime_utc).fromNow()}
        </Text>
        {donation ? (
          <View style={tw`flex-row items-center mt-1`}>
            <Ionicons name="heart" size={14} color={colors.brand} />
            <Text style={[text.body, tw`ml-1`]}>{donation.message}</Text>
          </View>
        ) : (
          <Text style={[text.body, tw`mt-1`]}>{item.message}</Text>
        )}
        {item.image_filename && (
          <Image
            resizeMode="contain"
            source={{ uri: resizedImageUrl(item.image_filename) }}
            style={tw`w-full max-w-[300px] h-[300px] mt-2`}
          />
        )}

        <IconButton
          icon="trash-outline"
          label={t("Delete this message")}
          onPress={onMessageDelete(item.id)}
          style={styles.deleteMessage}
        />
      </View>
    );
  };

  const mode = !event.guestbook_enabled
    ? "off"
    : event.prefer_chat
    ? "chat"
    : "guestbook";

  return (
    <SettingsPage title={guestbookTitle}>
      {canChangeMode && (
        <Formik
          enableReinitialize
          initialValues={{
            guestbook_enabled: event.guestbook_enabled !== false,
            prefer_chat: !!event.prefer_chat,
          }}
          onSubmit={async (values) => {
            try {
              const updatedEvent = await updateEventPrivacy({
                slug,
                event_privacy: event.event_privacy,
                guestbook_enabled: values.guestbook_enabled,
                event_password: event.event_password,
                download_enabled: event.download_enabled,
                prefer_chat: values.prefer_chat,
                hide_viewer_count: event.hide_viewer_count,
                email_pref: event.email_pref,
                live_only: event.live_only,
              });

              setEvent((currentEvent) => ({
                ...currentEvent,
                ...updatedEvent,
              }));
            } catch (err) {
              console.error(err);
              throw new Error(t("Could not update messaging settings"));
            }
          }}
        >
          {(f) => (
            <SettingsSection
              first
              title={t("Guest messages")}
              help="guestbook.mode"
            >
              <AutoSave {...f} />
              <ChoiceGroup
                accessibilityLabel={t("Guest messages")}
                value={
                  !f.values.guestbook_enabled
                    ? "off"
                    : f.values.prefer_chat
                    ? "chat"
                    : "guestbook"
                }
                onChange={(value) => {
                  if (value === "off") {
                    f.setFieldValue("guestbook_enabled", false);
                    return;
                  }
                  f.setFieldValue("guestbook_enabled", true);
                  f.setFieldValue("prefer_chat", value === "chat");
                }}
                options={[
                  {
                    value: "guestbook",
                    label: t("Guestbook"),
                    description: t(
                      "Guests sign it and add photos. Newest messages first."
                    ),
                  },
                  {
                    value: "chat",
                    label: t("Chat"),
                    description: t(
                      "A live chat next to the video, like a group conversation."
                    ),
                  },
                  {
                    value: "off",
                    label: t("Disable messaging"),
                    description: t("Guests can watch but can't post messages."),
                  },
                ]}
              />
            </SettingsSection>
          )}
        </Formik>
      )}

      {mode !== "off" && canPin && (
        <SettingsSection title={t("Pinned message")} help="guestbook.pinned">
          <GuestbookPinnedMessageForm event={event} />
        </SettingsSection>
      )}

      <SettingsSection
        first={!canChangeMode && !(mode !== "off" && canPin)}
        title={t("Messages")}
        help="guestbook.messages"
        description={
          messages.length
            ? tn("{count} message", "{count} messages", messages.length)
            : undefined
        }
      >
        {!messages.length && (
          <Text style={text.hint}>
            {loading
              ? t("Loading messages…")
              : t("No messages yet. They'll appear here as guests post them.")}
          </Text>
        )}
        {messages.filter((a) => a.pinned).map(renderMessage)}
        {messages.filter((a) => !a.pinned).map(renderMessage)}
      </SettingsSection>
    </SettingsPage>
  );
};

const styles = StyleSheet.create({
  // Top right of the message bubble (the bubble keeps room for it).
  deleteMessage: { position: "absolute", top: 8, right: 8 },
});

export default GuestBook;
